import { Business, getStaticFilter } from '@/api/models';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import * as React from 'react';

interface BusinessReviewsTabProps {
  business: Business;
  onToggleReview: (
    businessId: number,
    reviewId: number,
    isEvaluated: boolean,
  ) => void;
  onToggleScoreInclusion: (
    businessId: number,
    reviewId: number,
    isIncludedInAverage: boolean,
  ) => void;
  isTogglingReview: boolean;
  isTogglingScoreInclusion: boolean;
}

export const BusinessReviewsTab: React.FC<BusinessReviewsTabProps> = ({
  business,
  onToggleReview,
  onToggleScoreInclusion,
  isTogglingReview,
  isTogglingScoreInclusion,
}) => {
  const reviews = business.reviews || [];

  if (reviews.length === 0) {
    return (
      <div className='p-6 text-center'>
        <p className='text-gray-500'>No reviews for this business yet.</p>
      </div>
    );
  }

  return (
    <div className='p-6 space-y-4'>
      <div className='text-sm text-gray-600 mb-4'>
        Manage review visibility. Hidden reviews won't appear to users.
      </div>

      {reviews.map(review => (
        <div
          key={review.id}
          className={`p-4 rounded-lg border ${
            review.isEvaluated
              ? 'bg-white border-gray-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className='flex items-start justify-between'>
            <div className='flex-1'>
              {/* User Info */}
              <div className='flex items-center space-x-3 mb-2'>
                <div className='w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-semibold'>
                  {review.user?.firstName?.[0]}
                  {review.user?.lastName?.[0]}
                </div>
                <div>
                  <p className='font-medium text-gray-900'>
                    {review.user?.firstName} {review.user?.lastName}
                  </p>
                  <p className='text-xs text-gray-500'>
                    {review.createdAt
                      ? new Date(review.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : 'Unknown date'}
                  </p>
                </div>
              </div>

              {/* Review Text */}
              {review.review && (
                <p className='text-gray-700 text-sm mb-3'>{review.review}</p>
              )}

              {/* Filter Ratings */}
              {review.filters && review.filters.length > 0 && (
                <div className='flex flex-wrap gap-1'>
                  {review.filters.map((rf, index) => {
                    const staticFilter = getStaticFilter(rf.filter?.name || '');
                    return (
                      <span
                        key={index}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          rf.level === 'High'
                            ? 'bg-green-100 text-green-700'
                            : rf.level === 'Medium'
                              ? 'bg-yellow-100 text-yellow-700'
                              : rf.level === 'Low'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {staticFilter?.emoji}{' '}
                        {staticFilter?.label || rf.filter?.name}: {rf.level}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Review Status */}
            <div className='ml-4 flex flex-col items-end space-y-2'>
              {/* Approved/Visibility Toggle */}
              <div className='flex flex-col items-end'>
                <span
                  className={`text-xs font-medium mb-1 ${
                    review.isEvaluated ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {review.isEvaluated ? 'Approved' : 'Not Approved'}
                </span>
                <ToggleSwitch
                  value={review.isEvaluated || false}
                  onChange={() =>
                    onToggleReview(
                      business.id!,
                      review.id!,
                      !review.isEvaluated,
                    )
                  }
                  disabled={isTogglingReview}
                />
              </div>

              {/* Included in Score Toggle */}
              <div className='flex flex-col items-end'>
                <span
                  className={`text-xs font-medium mb-1 ${
                    review.isIncludedInAverage
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}
                >
                  {review.isIncludedInAverage ? 'In Score' : 'Excluded'}
                </span>
                <ToggleSwitch
                  value={review.isIncludedInAverage || false}
                  onChange={() =>
                    onToggleScoreInclusion(
                      business.id!,
                      review.id!,
                      !review.isIncludedInAverage,
                    )
                  }
                  disabled={isTogglingScoreInclusion}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
